import { DeleteOutlined, EditOutlined, DatabaseOutlined, SyncOutlined } from "@ant-design/icons";
import { Card, Table, Button, Descriptions, Space, Tooltip, Popconfirm, Tag, Switch } from "antd";
import axios from "axios";
import { useState } from "react";

import { selectFormOptions } from "../../../../../utils/helper";
import {
  showErrorNotification,
  showWarningNotification,
  showSuccessNotification
} from "../../../../../utils/notification";
import CreateEditLectureModal from "../../Modals/CreateEditLectureModal";
import CreateEditSetModal from "../../Modals/CreateEditSetModal";
import LectureTranscriptIngestionDetailsModal from "../../Modals/LectureTranscriptIngestionDetailsModal";
import { formatPreviousSlugs } from "../../SlugFormItem";

const LectureSetDetails = ({
  lectureSetData,
  courseEducators = [],
  lectureTypes = [],
  refreshContentTree = () => {},
  ROOT_URL,
  BASE_URL,
  adminConfig
}) => {
  const LECTURE_SET_API = `${ROOT_URL}/${BASE_URL}/lecture-set`;
  const LECTURES_API = `${LECTURE_SET_API}/lectures`;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditSetModalOpen, setIsEditSetModalOpen] = useState(false);
  const [selectedLecture, setSelectedLecture] = useState(null);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshingLectureId, setRefreshingLectureId] = useState(null);

  const handleReingestTranscript = (lecture) => {
    if (!lecture) return;
    setLoading(true);

    axios
      .post(`${LECTURES_API}/reset-transcript-ingestion`, {
        uniquePublicId: lecture.uniquePublicId
      })
      .then(() => {
        showSuccessNotification("Transcript re-ingestion initiated.");
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
        setIsEmbedModalOpen(false);
      });
  };

  const handleCreateLecture = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      lectureSetUniquePublicId: lectureSetData.uniquePublicId
    };
    axios
      .post(`${LECTURES_API}/create`, payload)
      .then(() => {
        setIsModalOpen(false);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateLecture = (values, lecture = selectedLecture) => {
    setLoading(true);
    const payload = {
      ...values,
      uniquePublicId: lecture.uniquePublicId
    };

    axios
      .post(`${LECTURES_API}/update`, payload)
      .then(() => {
        setIsModalOpen(false);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteLecture = (lecture) => {
    setLoading(true);
    const payload = {
      uniquePublicId: lecture.uniquePublicId
    };

    axios
      .post(`${LECTURES_API}/delete`, payload)
      .then(() => {
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRefreshLectureTpStreamMetadata = (lecture) => {
    if (!lecture) {
      return;
    }
    setRefreshingLectureId(lecture.uniquePublicId);

    axios
      .post(`${LECTURES_API}/refresh-tp-stream-metadata`, {
        uniquePublicId: lecture.uniquePublicId
      })
      .then(() => {
        showSuccessNotification("TP Streams metadata refreshed.");
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setRefreshingLectureId(null);
      });
  };

  const handleUploadLectureAsset = async (file, lectureUniquePublicId, assetType) => {
    if (assetType === "TRANSCRIPT" && file.type !== "text/vtt") {
      showWarningNotification("Only VTT files are allowed.");
      throw new Error("Only VTT files are allowed.");
    }
    if (assetType === "SLIDE" && file.type !== "application/pdf") {
      showWarningNotification("Only PDF files are allowed.");
      throw new Error("Only PDF files are allowed.");
    }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("uniquePublicId", lectureUniquePublicId);
    fd.append("assetType", assetType);

    try {
      const response = await axios.post(`${LECTURES_API}/upload-asset`, fd);
      return response.data;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  };

  const handleUpdateLectureSet = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      uniquePublicId: lectureSetData.uniquePublicId
    };

    axios
      .post(`${LECTURE_SET_API}/update`, payload)
      .then(() => {
        setIsEditSetModalOpen(false);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBulkPreviewToggle = (isPreviewAvailable) => {
    setLoading(true);
    axios
      .patch(`${LECTURE_SET_API}/toggle-preview`, {
        lectureSetUniquePublicId: lectureSetData.uniquePublicId,
        isPreviewAvailable
      })
      .then(() => {
        showSuccessNotification(
          `Preview ${isPreviewAvailable ? "enabled" : "disabled"} for all lectures.`
        );
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns = [
    {
      title: "Lecture Name",
      dataIndex: "title",
      key: "title",
      width: 220
    },
    {
      title: "Type",
      dataIndex: "lectureType",
      key: "lectureType",
      // Lectures created before lectureType existed do not have it.
      render: (lectureType = lectureTypes[0] || "THEORY") => {
        const label = selectFormOptions([lectureType])[0]?.label || lectureType;
        return <Tag color="blue">{label}</Tag>;
      }
    },
    {
      title: "Transcript Embedding Status",
      key: "embedStatus",
      render: (_, record) => {
        const metadata = record.aiDoubtSupportMetadata || {};
        const { transcriptFileUrl, ingestionStatus } = metadata;

        if (!transcriptFileUrl) {
          return <Tag color="default">NOT APPLICABLE</Tag>;
        }

        const status = (ingestionStatus || "PENDING").toUpperCase();
        let color = "processing";

        if (status === "COMPLETED") color = "success";
        else if (status === "FAILED") color = "error";
        else if (status === "PENDING") color = "warning";
        else color = "processing";

        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      render: (text) => text || "N/A"
    },
    {
      title: "Asset ID",
      dataIndex: "tpStreamsAssetId",
      key: "tpStreamsAssetId",
      render: (text) => text || "N/A"
    },
    {
      title: "Public ID",
      dataIndex: "uniquePublicId",
      key: "uniquePublicId",
      render: (text) => text || "N/A"
    },
    {
      title: "Order",
      dataIndex: "lectureSetDisplayOrder",
      key: "lectureSetDisplayOrder"
    },
    {
      title: "Visibility",
      dataIndex: "isVisible",
      key: "isVisible",
      render: (val) => (val ? "Visible" : "Hidden")
    },
    {
      title: "Preview",
      dataIndex: "isPreviewAvailable",
      key: "isPreviewAvailable",
      render: (val, record) => (
        <Tooltip title="When ON, non-enrolled users can see the title and watch the video for free.">
          <Switch
            checked={val}
            onChange={(checked) =>
              handleUpdateLecture({ ...record, isPreviewAvailable: checked }, record)
            }
            size="small"
          />
        </Tooltip>
      )
    },
    {
      title: "Educator",
      dataIndex: "educator",
      key: "educator"
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy"
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedLecture(record);
                setIsModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Refresh TP Streams Metadata">
            <Button
              type="text"
              icon={<SyncOutlined />}
              loading={refreshingLectureId === record.uniquePublicId}
              onClick={() => handleRefreshLectureTpStreamMetadata(record)}
            />
          </Tooltip>
          {adminConfig?.courseContentManagement?.hasAiBrainManagementAccess && (
            <Tooltip title="Embed Details">
              <Button
                type="text"
                icon={<DatabaseOutlined />}
                onClick={() => {
                  setSelectedLecture(record);
                  setIsEmbedModalOpen(true);
                }}
              />
            </Tooltip>
          )}
          <Popconfirm
            title="Are you sure you want to delete this lecture?"
            onConfirm={() => handleDeleteLecture(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Card
        title={lectureSetData.title}
        extra={
          <Space wrap>
            <Button
              type="primary"
              onClick={() => {
                setSelectedLecture(null);
                setIsModalOpen(true);
              }}
            >
              + Create Lecture
            </Button>
            <Button onClick={() => setIsEditSetModalOpen(true)}>Edit Metadata</Button>
            <Popconfirm
              title={`Are you sure you want to ${
                lectureSetData.isPreviewAvailable ? "disable" : "enable"
              } preview for all lectures?`}
              onConfirm={() => handleBulkPreviewToggle(!lectureSetData.isPreviewAvailable)}
            >
              <Button loading={loading}>
                {lectureSetData.isPreviewAvailable ? "Disable All Previews" : "Enable All Previews"}
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }} style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Title">{lectureSetData.title}</Descriptions.Item>
          <Descriptions.Item label="ID">{lectureSetData.uniquePublicId}</Descriptions.Item>
          <Descriptions.Item label="Slug">{lectureSetData.slug || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Previous Slugs">
            {formatPreviousSlugs(lectureSetData)}
          </Descriptions.Item>
          <Descriptions.Item label="Display Order In Module">
            {lectureSetData.moduleDisplayOrder}
          </Descriptions.Item>
          <Descriptions.Item label="Visibility">
            {lectureSetData.isVisible ? "Visible" : "Hidden"}
          </Descriptions.Item>
          <Descriptions.Item label="Preview Available">
            {lectureSetData.isPreviewAvailable ? (
              <Tag color="blue">Yes</Tag>
            ) : (
              <Tag color="default">No</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Open by Default">
            {lectureSetData.isOpenByDefault ? (
              <Tag color="blue">Yes</Tag>
            ) : (
              <Tag color="default">No</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Num Lectures">
            {lectureSetData.numLectures || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Created By">
            {lectureSetData.createdBy || "N/A"}
          </Descriptions.Item>
        </Descriptions>

        <Table
          dataSource={lectureSetData.items || []}
          columns={columns}
          pagination={false}
          rowKey="uniquePublicId"
          scroll={{ x: "max-content" }}
        />
      </Card>

      <CreateEditLectureModal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedLecture(null);
        }}
        courseEducators={courseEducators}
        lectureTypes={lectureTypes}
        onCreate={selectedLecture ? handleUpdateLecture : handleCreateLecture}
        loading={loading}
        initialValues={selectedLecture}
        isEdit={!!selectedLecture}
        onFileUpload={handleUploadLectureAsset}
      />

      <CreateEditSetModal
        open={isEditSetModalOpen}
        onCancel={() => setIsEditSetModalOpen(false)}
        onUpdate={handleUpdateLectureSet}
        loading={loading}
        entityName="Lecture Set"
        initialValues={{
          title: lectureSetData.title,
          slug: lectureSetData.slug,
          reservedSlugs: lectureSetData.reservedSlugs,
          moduleDisplayOrder: lectureSetData.moduleDisplayOrder,
          isVisible: lectureSetData.isVisible,
          isOpenByDefault: lectureSetData.isOpenByDefault
        }}
      />

      <LectureTranscriptIngestionDetailsModal
        open={isEmbedModalOpen}
        onCancel={() => {
          setIsEmbedModalOpen(false);
          setSelectedLecture(null);
        }}
        lecture={selectedLecture}
        onReingest={handleReingestTranscript}
        loading={loading}
        lecturesApi={LECTURES_API}
      />
    </>
  );
};

export default LectureSetDetails;
